import { Card, CardContent, Box, Typography, Chip, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { ItemType, ItemTypeWithLocations } from '../schema';
import { useState, useMemo } from 'react';
import { calculateItemLocations } from '../utils/inventory';
import ItemLocationBreakdown from './ItemLocationBreakdown';

interface Props {
    itemType: ItemType | ItemTypeWithLocations;
    onEdit?: (itemType: ItemType) => void;
    onDelete?: (itemType: ItemType) => void;
    onViewDetails?: (itemType: ItemType) => void;
    onRecordPurchase?: (itemType: ItemType) => void;
    onRecordLostOrBroken?: (itemType: ItemType) => void;
    onRecordConsumption?: (itemType: ItemType) => void;
    onAdjustQuantity?: (itemType: ItemType) => void;
}

export default function ItemTypeRow({
    itemType,
    onEdit,
    onDelete,
    onViewDetails,
    onRecordPurchase,
    onRecordLostOrBroken,
    onRecordConsumption,
    onAdjustQuantity,
}: Props) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const locationData = useMemo(() => {
        const itemTypeWithLocations = itemType as ItemTypeWithLocations;
        const locations =
            'itemLocations' in itemTypeWithLocations
                ? ((itemTypeWithLocations.itemLocations || []) as Array<{
                      id: string;
                      quantity: number;
                      event: { id: string; name: string } | null | undefined;
                      storageLocation?: { id: string; name: string } | null;
                  }>)
                : [];
        return calculateItemLocations(itemType, locations);
    }, [itemType]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        onEdit?.(itemType);
    };

    const handleDelete = () => {
        handleMenuClose();
        onDelete?.(itemType);
    };

    const handleViewDetails = () => {
        handleMenuClose();
        onViewDetails?.(itemType);
    };

    const handleRecordPurchase = () => {
        handleMenuClose();
        onRecordPurchase?.(itemType);
    };

    const handleRecordLostOrBroken = () => {
        handleMenuClose();
        onRecordLostOrBroken?.(itemType);
    };

    const handleRecordConsumption = () => {
        handleMenuClose();
        onRecordConsumption?.(itemType);
    };

    const handleAdjustQuantity = () => {
        handleMenuClose();
        onAdjustQuantity?.(itemType);
    };

    const photoUrl = itemType.photoUrl;
    const category = itemType.category;
    const categoryColor = category?.color || '#2196F3';

    return (
        <Card
            elevation={2}
            sx={{
                '&:hover': {
                    boxShadow: 3,
                },
                cursor: 'pointer',
                transition: 'box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                },
            }}
            onClick={handleViewDetails}
        >
            <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Photo */}
                    <Avatar
                        src={photoUrl || undefined}
                        variant="rounded"
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: 'grey.200',
                        }}
                    >
                        {!photoUrl && <LocalOfferIcon />}
                    </Avatar>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                                {itemType.name}
                            </Typography>
                            {itemType.consumable && <Chip label="Consumable" size="small" color="secondary" sx={{ fontSize: '0.7rem' }} />}
                            {category && (
                                <Chip
                                    label={category.name}
                                    size="small"
                                    sx={{
                                        bgcolor: categoryColor,
                                        color: 'white',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                        </Box>

                        {itemType.description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {itemType.description}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                Total: {locationData.total}
                            </Typography>
                            <ItemLocationBreakdown locationData={locationData} />
                        </Box>
                    </Box>

                    {/* Actions Menu */}
                    <IconButton
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            handleMenuOpen(e);
                        }}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>
            </CardContent>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleViewDetails}>
                    <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
                    View Details
                </MenuItem>
                <MenuItem onClick={handleEdit}>
                    <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
                    Delete
                </MenuItem>
                <MenuItem onClick={handleRecordPurchase}>
                    <ShoppingCartIcon sx={{ mr: 1, fontSize: 20 }} />
                    Purchase
                </MenuItem>
                <MenuItem onClick={handleRecordLostOrBroken}>
                    <WarningAmberIcon sx={{ mr: 1, fontSize: 20 }} />
                    Lost / Broken
                </MenuItem>
                {onAdjustQuantity && (
                    <MenuItem onClick={handleAdjustQuantity}>
                        <TuneIcon sx={{ mr: 1, fontSize: 20 }} />
                        Adjust Quantity
                    </MenuItem>
                )}
                {itemType.consumable && onRecordConsumption && (
                    <MenuItem onClick={handleRecordConsumption}>
                        <LocalGasStationIcon sx={{ mr: 1, fontSize: 20 }} />
                        Consume
                    </MenuItem>
                )}
            </Menu>
        </Card>
    );
}
